<template>
  <header data-bs-theme="dark" class="p-4">
    <nav class="navbar navbar-expand-md navbar-dark fixed-top bg-dark">
      <div class="container-fluid">
        <a class="navbar-brand" href="#">
          <font-awesome-icon icon="fa-solid fa-dumbbell" />
          STRN.GR
        </a>
        <!-- Burger button -->
        <button
          class="navbar-toggler"
          type="button"
          data-bs-toggle="collapse"
          data-bs-target="#navbarCollapse"
          aria-controls="navbarCollapse"
          aria-expanded="false">
          <span class="navbar-toggler-icon" />
        </button>
        <div class="collapse navbar-collapse" id="navbarCollapse">
          <!-- LEFT SIDE NAV (auth only) -->
          <ul class="navbar-nav me-auto mb-2 mb-md-0" v-if="user">
            <li class="nav-item">
              <router-link to="/today" class="nav-link link-light">
                {{ $t('links.today') }}
              </router-link>
            </li>
            <li class="nav-item">
              <router-link to="/feed" class="nav-link link-light">
                {{ $t('links.journal') }}
              </router-link>
            </li>
            <li class="nav-item">
              <router-link to="/measurements" class="nav-link link-light">
                {{ $t('measurements.title') }}
              </router-link>
            </li>
            <li class="nav-item">
              <router-link to="/trends" class="nav-link link-light">
                {{ $t('trends.title') }}
              </router-link>
            </li>
            <li class="nav-item">
              <a
                class="nav-link link-primary"
                @click="showModal = true"
                @keypress="showModal = true"
                style="cursor: pointer;">
                {{ $t('links.feedback') }}
              </a>
            </li>
          </ul>
          <userLang v-if="!user" btnstyle="btn btn-outline-light" />
          <!-- COLLAPSED AREA (auth button always shown here on small screens) -->
          <ul class="navbar-nav d-md-none ms-auto mt-2">
            <li class="nav-item" v-if="!user">
              <router-link class="nav-item link-body-emphasis link-light" to="/login">
                {{ $t('links.login') }}
              </router-link>
            </li>
            <li v-else>
              <router-link class="dropdown-item link-light" to="/profile">
                {{ $t('links.profile') }}
              </router-link>
            </li>
          </ul>
        </div>

        <!-- RIGHT SIDE STATIC AREA (desktop only) -->
        <div class="d-none d-md-flex align-items-center ms-auto">
          <!-- Avatar dropdown if user -->
          <div class="dropdown me-2" v-if="user">
            <a
              href="#"
              class="dropdown-toggle d-flex align-items-center
              text-white text-decoration-none link-light"
              data-bs-toggle="dropdown"
              aria-expanded="false">
              <img :src="user.picture" alt="pic" width="32" height="32" class="rounded-circle" />
            </a>
            <ul class="dropdown-menu dropdown-menu-end">
              <li>
                <router-link class="dropdown-item link-light" to="/profile">
                  <font-awesome-icon icon="fa-solid fa-address-card" />
                  {{ $t('links.profile') }}
                </router-link>
              </li>
              <li>
                <hr class="dropdown-divider" />
              </li>
              <li>
                <AuthButton />
              </li>
            </ul>
          </div>

          <!-- Auth button for desktop if not logged in -->
          <div v-if="!user">
            <router-link class="dropdown-item link-light" to="/login">
              <font-awesome-icon icon="fa-solid fa-address-card" />
              {{ $t('links.login') }}
            </router-link>
          </div>
        </div>
      </div>
    </nav>
  </header>
  <ToastComponent ref="toastRef" />
  <FeedbackModal :isOpen="showModal" @close="showModal = false" />
  <div class="bg-body-tertiary">
    <router-view />
  </div>
  <footer class="py-1 my-1">
    <ul class="nav justify-content-center border-bottom pb-1 mb-1">
      <li class="nav-item" v-if="!user">
        <router-link class="nav-link px-2 text-body-secondary" to="/login">
          {{ $t('links.login') }}
        </router-link>
      </li>
      <li class="nav-item" v-if="user">
        <router-link class="nav-link px-2 text-body-secondary" to="/today">
          {{ $t('links.today') }}
        </router-link>
      </li>
      <li class="nav-item" v-if="user">
        <router-link class="nav-link px-2 text-body-secondary" to="/feed">
          {{ $t('links.journal') }}
        </router-link>
      </li>
      <li class="nav-item" v-if="user">
        <router-link class="nav-link px-2 text-body-secondary" to="/measurements">
          {{ $t('measurements.title') }}
        </router-link>
      </li>
      <li class="nav-item" v-if="user">
        <router-link class="nav-link px-2 text-body-secondary" to="/trends">
          {{ $t('trends.title') }}
        </router-link>
      </li>
      <li class="nav-item" v-if="user">
        <router-link class="nav-link px-2 text-body-secondary" to="/profile">
          {{ $t('links.profile') }}
        </router-link>
      </li>
      <li v-if="user">
        <a
          class="nav-link px-2 text-body-secondary"
          @click="showModal = true"
          @keypress="showModal = true"
          style="cursor: pointer;">
          {{ $t('links.feedback') }}
        </a>
      </li>
      <li class="nav-item">
        <router-link class="nav-link px-2 text-body-secondary" to="/about">
          {{ $t('links.about') }}
        </router-link>
      </li>
    </ul>
    <p class="text-center text-body-secondary"><a :href="`mailto:${$t('info.email')}`">{{ $t('info.email') }}</a></p>
    <p class="text-center text-body-secondary">&copy; STRN.GR</p>
  </footer>
</template>

<script setup>
import { storeToRefs } from 'pinia';
import { onMounted, ref } from 'vue';
import { useUserStore } from './stores/userStore';
import userLang from './components/common/userLang.vue';
import ToastComponent from './components/common/toastComponent.vue';
import AuthButton from './components/common/GoogleAuthButton.vue';
import FeedbackModal from './components/FeedBack.vue';

const toastRef = ref(null);
const showModal = ref(false);

const userStore = useUserStore();
const { user } = storeToRefs(userStore);
onMounted(() => {
  window.$toast = toastRef.value;
});
</script>
<style>
.display-1,
.display-2,
.display-3,
.display-4,
.display-5,
.display-6 {
  font-family: 'Space Grotesk', sans-serif !important;
}
</style>
